import Head from 'next/head';
import { useRouter } from 'next/router';
import type { NextPageContext } from 'next';
import { useEffect, useState } from 'react';

import type { BlogPostFragment, SeoData } from 'types';
import type { BlogCategory } from '@root/sanity/sanity.types';
import { PAGE_BLOG } from '@root/constants/routes';
import { BLOG_POSTS_PER_PAGE } from '@root/constants/blog';
import BlogList from 'components/Blog/List';
import BlogPagination from 'components/Blog/Pagination';
import { getCanonicalUrlForCategory } from 'components/Blog/utils';
import NextSteps from 'components/NextSteps';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import SalesCalloutInspiration from 'components/SalesCalloutInspiration';
import LayoutBlog from 'layout/Blog';
import { getAllBlogCategories, getBlogIndex, getBlogPostsForCategory } from 'services/blog';
import { withPageStaticProps } from 'utils/header';
import { getParameterByName } from 'utils/utils';

type BlogCategoryPageProps = {
  activeBlogCategory: BlogCategory;
  blogCategories: BlogCategory[];
  canonicalUrl: string;
  error: boolean;
  posts: BlogPostFragment[];
  seo: SeoData;
  title: string;
  totalPosts: number;
};

export type BlogCategoryDownstreamProps = BlogCategoryPageProps & {
  page: number;
  totalPages: number;
  updatePage: (options: { categoryName: string; page: number }) => void;
};

export const getTotalPages = (totalPosts: number) => Math.ceil(totalPosts / BLOG_POSTS_PER_PAGE);

/**
 * Blog Category Page
 */
const BlogCategoryPage = ({ activeBlogCategory, blogCategories, posts, title, totalPosts }: BlogCategoryPageProps) => {
  const [page, setPage] = useState(1);
  const router = useRouter();

  useEffect(() => {
    const qsPage = getParameterByName('page') ? Number(getParameterByName('page')) : page;

    if (qsPage && qsPage !== page) {
      setPage(qsPage);
    }
  }, []);

  const getRenderedPosts = () => {
    const offsetStart = (page - 1) * BLOG_POSTS_PER_PAGE;
    const offsetEnd = offsetStart + BLOG_POSTS_PER_PAGE;

    return posts.slice(offsetStart, offsetEnd);
  };

  const handleUpdatePage = ({ categoryName, page }: { categoryName: string; page: number }) => {
    router.push({
      pathname: `${PAGE_BLOG}/${categoryName}`,
      query: {
        page,
      },
    });

    setPage(page);
  };

  const totalPages = getTotalPages(totalPosts);
  const renderedPosts = getRenderedPosts();

  const pathname =
    activeBlogCategory.slug!.current === 'all' ? PAGE_BLOG : `${PAGE_BLOG}/${activeBlogCategory.slug!.current}`;

  return (
    <>
      <Head>
        <title>{title ?? 'Generation Tux Blog'}</title>
      </Head>
      <LayoutBlog
        activeBlogCategory={activeBlogCategory}
        categories={blogCategories}
        pathname={pathname}
        showLogo={true}
      >
        <BlogList activeBlogCategory={activeBlogCategory.slug.current} posts={renderedPosts} />
        <BlogPagination
          activeBlogCategory={activeBlogCategory.slug.current}
          page={page}
          totalPages={totalPages}
          updatePage={handleUpdatePage}
        />
      </LayoutBlog>

      <NextSteps
        ctaPageName="blog"
        salesCallout1={<SalesCalloutSwatches ctaPageName="blog" />}
        salesCallout2={<SalesCalloutInspiration ctaPageName="blog" />}
      />
    </>
  );
};

// This function gets called at build time
export const getStaticPaths = async () => {
  const allCategories = await getAllBlogCategories();

  const paths = allCategories.map(({ slug }) => ({
    params: {
      category: slug?.current,
    },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

type Context = NextPageContext & {
  params: {
    category: string;
  };
};

export const getStaticProps = withPageStaticProps(async ({ params }: Context) => {
  try {
    const { category } = params;
    const blogCategories = await getAllBlogCategories();
    const activeBlogCategory = blogCategories.find(({ slug }) => slug!.current === category);

    const blogIndex = await getBlogIndex();
    const posts = await getBlogPostsForCategory(activeBlogCategory);
    const totalPosts = posts.length;
    const canonicalUrl = getCanonicalUrlForCategory(activeBlogCategory);
    const title = `${activeBlogCategory?.categoryName} - ${blogIndex.allBlogSettings[0].title}`;
    const slug = `${PAGE_BLOG}/${activeBlogCategory?.slug.current}`;

    return {
      props: {
        activeBlogCategory,
        blogCategories,
        canonicalUrl,
        error: false,
        posts,
        seo: {
          title,
          metaDescription: title,
          slug,
        },
        title,
        totalPosts,
      },
    };
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get blog posts');
  }
});

export default BlogCategoryPage;
